import React, { useEffect, useState } from 'react';
import { TriangleAlert, Construction, ChevronDown, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';

const T = {
  es: { until: 'hasta', today: 'hoy', tomorrow: 'mañana', more: 'Leer más', showMore: 'Ver más avisos', showLess: 'Ver menos', types: { tiempo: 'Aviso meteorológico', sendero: 'Aviso de sendero', movilidad: 'Movilidad' }, levels: { prealerta: 'Prealerta', aviso: 'Aviso', alerta: 'Alerta', alerta_maxima: 'Alerta máxima' } },
  en: { until: 'until', today: 'today', tomorrow: 'tomorrow', more: 'Read more', showMore: 'More alerts', showLess: 'Show less', types: { tiempo: 'Weather alert', sendero: 'Trail alert', movilidad: 'Mobility' }, levels: { prealerta: 'Pre-alert', aviso: 'Advisory', alerta: 'Alert', alerta_maxima: 'Maximum alert' } },
  de: { until: 'bis', today: 'heute', tomorrow: 'morgen', more: 'Mehr lesen', showMore: 'Weitere Hinweise', showLess: 'Weniger', types: { tiempo: 'Wetterwarnung', sendero: 'Wanderweg-Hinweis', movilidad: 'Mobilität' }, levels: { prealerta: 'Voralarm', aviso: 'Hinweis', alerta: 'Alarm', alerta_maxima: 'Höchste Alarmstufe' } },
};
const LOCALE = { es: 'es-ES', en: 'en-GB', de: 'de-DE' };
const OCRE = { bg: '#FBF1DF', border: '#C4620C', text: '#7A3E08', icon: '#C4620C' };
const PURPLE = { bg: '#EFE6F7', border: '#6B3FA0', text: '#3E2466', icon: '#6B3FA0' };
const dayKey = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Atlantic/Canary', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

export const alertPalette = (a) => (a.type === 'tiempo' && ['alerta', 'alerta_maxima'].includes(a.level) ? PURPLE : OCRE);

export const untilLabel = (iso, language) => {
  if (!iso) return null;
  const t = T[language] || T.es;
  const d = new Date(iso);
  const now = new Date();
  const time = new Intl.DateTimeFormat(LOCALE[language] || 'es-ES', { timeZone: 'Atlantic/Canary', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  const k = dayKey(d), today = dayKey(now), tomorrow = dayKey(new Date(now.getTime() + 864e5));
  if (k === today) return `${t.until} ${t.today}, ${time}`;
  if (k === tomorrow) return `${t.until} ${t.tomorrow}, ${time}`;
  const date = new Intl.DateTimeFormat(LOCALE[language] || 'es-ES', { timeZone: 'Atlantic/Canary', day: 'numeric', month: 'short' }).format(d).replace('.', '');
  return `${t.until} ${language === 'es' ? 'el ' : ''}${date}, ${time}`;
};

const field = (a, f, language) => a[`${f}_${language}`] || a[f];

export const AlertItem = ({ alert, language, compact = false }) => {
  const t = T[language] || T.es;
  const p = alertPalette(alert);
  const Icon = alert.type === 'movilidad' ? Construction : TriangleAlert;
  const until = untilLabel(alert.valid_until, language);
  const kind = alert.type === 'tiempo' && alert.level ? t.levels[alert.level] : t.types[alert.type];
  return (
    <div className={`flex gap-3 px-3.5 ${compact ? 'py-2' : 'py-3'} rounded-xl border-l-4 shadow-sm transition-colors`} style={{ backgroundColor: p.bg, borderColor: p.border }} role="status"
      data-testid={`alert-${alert.id}`} data-alert-type={alert.type} data-alert-level={alert.level || ''}>
      <Icon size={20} className="shrink-0 mt-0.5" style={{ color: p.icon }} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap text-[11px] font-semibold uppercase tracking-wide" style={{ color: p.icon }}>
          <span data-testid="alert-kind">{kind}</span>
          {until && <span className="normal-case tracking-normal font-medium" style={{ color: p.text }} data-testid="alert-until">· {until}</span>}
          {compact && alert.source_url && (
            <a href={alert.source_url} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal text-xs font-semibold hover:underline" style={{ color: p.icon }} data-testid="alert-read-more">
              {t.more} <ExternalLink size={11} />
            </a>
          )}
        </div>
        <p className={`font-semibold text-sm leading-snug mt-0.5 ${compact ? 'line-clamp-1' : ''}`} style={{ color: p.text }} data-testid="alert-title">
          {field(alert, 'title', language)}
        </p>
        {!compact && field(alert, 'summary', language) && <p className="text-xs mt-1 leading-relaxed" style={{ color: p.text, opacity: 0.85 }} data-testid="alert-summary">{field(alert, 'summary', language)}</p>}
        {!compact && alert.source_url && (
          <a href={alert.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold mt-1.5 hover:underline" style={{ color: p.icon }} data-testid="alert-read-more">
            {t.more} <ExternalLink size={11} />
          </a>
        )}
      </div>
    </div>
  );
};

export const AlertBand = ({ type, eventId, compact = false, className = '', max = 3 }) => {
  const { language } = useLanguage();
  const t = T[language] || T.es;
  const [alerts, setAlerts] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getAlerts(type, eventId).then((r) => { if (!cancelled) setAlerts(r.data); }).catch((e) => console.error('Error loading alerts:', e));
    return () => { cancelled = true; };
  }, [type, eventId]);

  if (!alerts.length) return null;
  const shown = showAll ? alerts : alerts.slice(0, max);
  return (
    <div className={`space-y-2 ${className}`} data-testid={`alert-band-${type}`}>
      {shown.map((a) => <AlertItem key={a.id} alert={a} language={language} compact={compact} />)}
      {alerts.length > max && (
        <button onClick={() => setShowAll((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900 px-1" data-testid="alert-band-toggle">
          {showAll ? t.showLess : `${t.showMore} (${alerts.length - max})`} <ChevronDown size={14} className={`transition-transform ${showAll ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
};

export default AlertBand;
